import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { AppContext } from "../../../App";
import ExchangeConnectionsSelect from "./ExchangeConnectionsSelect";
import ExchangerList from "./ExchangerList";
import ExchangerItem from "./ExchangerItem";
import ExchangerConfirm from "./ExchangerConfirm";
import Alert from "@material-ui/lab/Alert";
import { useTranslation } from "react-i18next";
import Notification from "../notification/Notification";

import { parseAxiosError } from "../../../utils/response";
import userAuthenticationConfig from "../../../utils/userAuthenticationConfig";
import { EXCHANGE_TYPE, HTTP_STATUSES, STANDARD_ERRORS_WAY } from "../../../utils/consts";
import eventBus from "../../../utils/eventBus.utils";

import { StyledExchangerSwap } from "./styledExchanger";

const ExchangerContainer = () => {
  const { user } = useContext(AppContext);

  const INITIAL_EXCHANGE_DATA = {
    "inCurrency": null,
    "inService": null,
    "outCurrency": null,
    "outService": null,
    "inAmount": "0",
    "outAmount": "0",
    "rate": null
  };

  const { t } = useTranslation("exchangerPage");

  const [inExchangePairUnit, setInExchangePairUnit] = useState([]);
  const [outExchangePairUnit, setOutExchangePairUnit] = useState([]);
  const [exchange, setExchange] = useState({ ...INITIAL_EXCHANGE_DATA });

  const [selectedInExchangePairUnit, setSelectedInExchangePairUnit] = useState(null);
  const [selectedOutExchangePairUnit, setSelectedOutExchangePairUnit] = useState(null);

  const [exchangeHistory, setExchangeHistory] = useState(null);
  const [updateAnimation, setUpdateAnimation] = useState(false);

  const [notification, setNotification] = useState({
    visible: false,
    type: "",
    message: ""
  });

  const [connections, setConnections] = useState(null);
  const [isPhysical, setIsPhysical] = useState(false);
  const [physicalExchangeData, setPhysicalExchangeData] = useState({
    inMinAmount: null,
    outMinAmount: null
  });

  const fetchExchangePairs = () => {
    axios.get("/api/calculator-exchange-pairs", userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK) {
        setInExchangePairUnit(response.data);
        setOutExchangePairUnit(response.data);
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const createExchange = () => {
    let data = {
      "inCurrency": exchange.inCurrency,
      "inService": exchange.inService,
      "outCurrency": exchange.outCurrency,
      "outService": exchange.outService,
      "inAmount": exchange.inAmount.toString(),
      "connectionId": exchange.connectionId
    };

    for (let key in data) {
      if (data[key] === null || data[key] === "") {
        return;
      }
    }

    axios.post("/api/exchanges", data, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_CREATED) {
        setExchange({ ...INITIAL_EXCHANGE_DATA });

        setSelectedInExchangePairUnit(null);
        setSelectedOutExchangePairUnit(null);
        setInExchangePairUnit([]);
        setIsPhysical(false);
        setOutExchangePairUnit([]);
        setPhysicalExchangeData({
          inMinAmount: null,
          outMinAmount: null
        });

        setNotification({
          ...notification,
          visible: true,
          type: "success",
          message: t("exchanger.success")
        });

        fetchExchangePairs();
        fetchExchangeHistory();

        eventBus.dispatch("exchangeEvent");
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const calculate = (amount, side, inCurrency = null, inService = null, outCurrency = null, outService = null) => {
    let data = {
      "inCurrency": inCurrency ?? exchange.inCurrency,
      "inService": inService ?? exchange.inService,
      "outCurrency": outCurrency ?? exchange.outCurrency,
      "outService": outService ?? exchange.outService,
      "amount": amount.toString(),
      "side": side,
      "isSelect": exchange?.isSelect ?? false
    };

    for (let key in data) {
      if (data[key] === null || data[key] === "") {
        return;
      }
    }

    axios.post("/api/calculate-exchange", data, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK) {
        setExchange((prevState) => (
          {
            ...prevState,
            inAmount: response.data.inAmount,
            outAmount: response.data.outAmount,
            rate: response.data.rate,
            unfinishedExchangeAmount: response.data?.unfinishedExchangeAmount,
            isSelect: false
          }
        ));

        setIsPhysical(response.data.isPhysical);

        if (response.data?.isPhysical) {
          setPhysicalExchangeData({
            inMinAmount: response.data.inMinAmount,
            outMinAmount: response.data.outMinAmount
          });

          setConnections(response.data.connections);
        }

        if (response.data?.error) {
          setNotification({ ...notification, visible: true, type: "error", message: response.data?.error });
        }
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const fetchExchangeHistory = () => {
    let items = 1;

    if (exchangeHistory) {
      items = 2;
    }

    axios.get("/api/exchanges?itemsPerPage=" + items, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK) {
        setExchangeHistory(response.data["hydra:member"]);
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    }).finally(startUpdateAnimation);
  };

  const startUpdateAnimation = () => {
    let animationDuration = 2500;

    setUpdateAnimation(true);

    setTimeout(() => {
      setUpdateAnimation(false);
    }, animationDuration);
  };

  const swap = () => {
    if (exchange.inService === null || exchange.outService === null) {
      return;
    }

    setExchange((prevState) => ({
      ...prevState,
      inCurrency: prevState.outCurrency,
      inService: prevState.outService,
      outCurrency: prevState.inCurrency,
      outService: prevState.inService,
      inAmount: prevState.outAmount,
      outAmount: prevState.inAmount
    }));

    setPhysicalExchangeData(prevState => ({
      inMinAmount: prevState.outMinAmount,
      outMinAmount: prevState.inMinAmount
    }));

    setSelectedInExchangePairUnit(selectedOutExchangePairUnit);
    setSelectedOutExchangePairUnit(selectedInExchangePairUnit);

    calculate(exchange.outAmount, EXCHANGE_TYPE.IN, exchange.outCurrency, exchange.outService, exchange.inCurrency, exchange.inService);
  };

  useEffect(() => {
    fetchExchangePairs();
  }, []);


  //TODO add pusher

  return (
    <>
      <Notification
        notification={notification}
        setNotification={setNotification}
      />
      <div className="exchanger-body">
        <ExchangerItem
          t={t}
          type={EXCHANGE_TYPE.IN}
          exchangePairUnit={inExchangePairUnit}
          exchange={exchange}
          setExchange={setExchange}
          selectedExchangePairUnit={selectedInExchangePairUnit}
          setSelectedExchangePairUnit={setSelectedInExchangePairUnit}
          calculate={calculate}
          physicalExchangeData={physicalExchangeData}
        />
        <StyledExchangerSwap onClick={swap}>
          <span className="icon-light-reload"></span>
        </StyledExchangerSwap>
        <ExchangerItem
          t={t}
          type={EXCHANGE_TYPE.OUT}
          exchangePairUnit={outExchangePairUnit}
          exchange={exchange}
          setExchange={setExchange}
          selectedExchangePairUnit={selectedOutExchangePairUnit}
          setSelectedExchangePairUnit={setSelectedOutExchangePairUnit}
          calculate={calculate}
          physicalExchangeData={physicalExchangeData}
        />
        {isPhysical && connections &&
          <ExchangeConnectionsSelect
            exchange={exchange}
            connections={connections}
            setExchange={setExchange}
          />
        }
        <Alert className="exchanger-alert" severity="warning">
          {t("exchanger.warning")}
        </Alert>
        {/*<Alert className="exchanger-alert" severity="warning">*/}
        {/*  {t("exchanger.reserveWarning")}*/}
        {/*</Alert>*/}
        <ExchangerConfirm
          t={t}
          exchange={exchange}
          createExchange={createExchange}
          physicalExchangeData={physicalExchangeData}
          isPhysical={isPhysical}
        />
      </div>
      <ExchangerList t={t} exchangeHistory={exchangeHistory} updateAnimation={updateAnimation} />
    </>
  );
};

export default ExchangerContainer;